import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUsers } from './hooks/useUsers';
import { UserMetricsCards, UsersTable, InviteUserModal, ViewUserModal } from './components';
import { UserTableItem } from './types';
import { PageLoader, FullWidthContainer, Input, Select, Button, Modal } from '../../components';
import { useMinimumLoader } from '../../hooks/useMinimumLoader';

const Users: React.FC = () => {
  const { t } = useTranslation();
  const {
    metrics,
    users,
    filters,
    setFilters,
    loading,
    error,
    inviteUser,
    editUser,
    deleteUser,
    fetchUserDetails,
  } = useUsers();

  const [showInviteModal, setShowInviteModal] = useState(false);
  const [viewUserModal, setViewUserModal] = useState<{
    isOpen: boolean;
    userId: string | null;
  }>({ isOpen: false, userId: null });
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    user: UserTableItem | null;
  }>({ isOpen: false, user: null });
  const showLoader = useMinimumLoader(loading, { minimumDuration: 500 });

  const filteredUsers = users.filter(user => {
    const matchesSearch =
      user.firstName.toLowerCase().includes(filters.search.toLowerCase()) ||
      user.lastName.toLowerCase().includes(filters.search.toLowerCase()) ||
      user.email.toLowerCase().includes(filters.search.toLowerCase());
    const matchesRole = filters.role === '' || user.role === filters.role;
    return matchesSearch && matchesRole;
  });

  const handleInviteUser = async (data: {
    email: string;
    role: 'admin' | 'manager' | 'user';
    firstName: string;
    lastName: string;
  }) => {
    await inviteUser(data);
  };

  const handleViewUser = (id: string) => {
    setViewUserModal({ isOpen: true, userId: id });
  };

  const handleDeleteUser = (id: string) => {
    const user = users.find(u => u.id === id);
    if (user) {
      setDeleteModal({ isOpen: true, user });
    }
  };

  const confirmDelete = async () => {
    if (deleteModal.user) {
      await deleteUser(deleteModal.user.id);
      setDeleteModal({ isOpen: false, user: null });
    }
  };

  const cancelDelete = () => {
    setDeleteModal({ isOpen: false, user: null });
  };

  if (showLoader) {
    return <PageLoader show={true} />;
  }

  if (error) {
    return (
      <FullWidthContainer>
        <div className="space-y-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{t('users.title')}</h1>
            <p className="text-gray-600 dark:text-gray-400 mt-2">{t('users.subtitle')}</p>
          </div>
          <div className="flex items-center justify-center py-12">
            <div className="text-red-500 text-center">
              <p className="text-lg font-semibold">{t('common.error')}</p>
              <p className="text-sm">{error}</p>
            </div>
          </div>
        </div>
      </FullWidthContainer>
    );
  }

  return (
    <FullWidthContainer>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{t('users.title')}</h1>
            <p className="text-gray-600 dark:text-gray-400 mt-2">{t('users.subtitle')}</p>
          </div>
          <Button
            variant="gradient-multi"
            onClick={() => setShowInviteModal(true)}
            className="flex items-center gap-2"
            disabled
          >
            <span className="material-icons">person_add</span>
            {t('users.inviteUser')}
          </Button>
        </div>

        {/* Metrics Cards */}
        <UserMetricsCards metrics={metrics} />

        {/* Users Table */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            {t('users.allUsers')}
          </h2>
          <div className="flex gap-4">
            <Input
              type="text"
              placeholder={t('users.searchUsers')}
              value={filters.search}
              onChange={e => setFilters({ ...filters, search: e.target.value })}
              className="w-80 h-10"
            />
            <Select
              value={filters.role}
              onChange={value =>
                setFilters({
                  ...filters,
                  role: Array.isArray(value)
                    ? (value[0] as '' | 'admin' | 'manager' | 'user' | 'owner')
                    : (value as '' | 'admin' | 'manager' | 'user' | 'owner'),
                })
              }
              options={[
                { value: '', label: t('users.allRoles') },
                { value: 'admin', label: t('users.admin') },
                { value: 'manager', label: t('users.manager') },
                { value: 'owner', label: t('users.owner') || 'Owner' },
                { value: 'user', label: t('users.user') },
              ]}
              className="w-40 h-10"
            />
          </div>
        </div>
        <UsersTable
          users={filteredUsers}
          onViewUser={handleViewUser}
          onEditUser={editUser}
          onDeleteUser={handleDeleteUser}
        />
      </div>

      {/* Invite User Modal */}
      <InviteUserModal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        onInvite={handleInviteUser}
      />

      {/* View User Modal */}
      <ViewUserModal
        isOpen={viewUserModal.isOpen}
        onClose={() => setViewUserModal({ isOpen: false, userId: null })}
        userId={viewUserModal.userId}
        onFetchUserDetails={fetchUserDetails}
      />

      {/* Delete User Modal */}
      <Modal
        isOpen={deleteModal.isOpen}
        onClose={cancelDelete}
        title=""
        size="sm"
        showCloseButton={false}
      >
        <div className="text-center space-y-6">
          {/* Warning Icon */}
          <div className="mx-auto w-16 h-16 bg-red-50 dark:bg-red-900/20 rounded-full flex items-center justify-center">
            <span className="material-icons text-3xl text-red-600 dark:text-red-400">
              delete_forever
            </span>
          </div>

          {/* Content */}
          <div className="space-y-3">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
              {t('users.deleteUser')}
            </h3>
            <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
              {t('users.areYouSureDeleteUser')}{' '}
              <span className="font-semibold text-gray-900 dark:text-white">
                &ldquo;{deleteModal.user?.firstName} {deleteModal.user?.lastName}&rdquo;
              </span>
              ?
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-500">
              {t('users.thisActionCannotBeUndoneUser')}
            </p>
          </div>

          {/* User Info Card */}
          {deleteModal.user && (
            <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-primary-100 dark:bg-primary-900/20 rounded-full flex items-center justify-center">
                  <span className="material-icons text-primary-600 dark:text-primary-400 text-sm">
                    person
                  </span>
                </div>
                <div className="text-left">
                  <p className="font-medium text-gray-900 dark:text-white">
                    {deleteModal.user.firstName} {deleteModal.user.lastName}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {deleteModal.user.email} •{' '}
                    {deleteModal.user.roleDisplay || deleteModal.user.role}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 pt-2">
            <Button
              variant="secondary"
              onClick={cancelDelete}
              className="flex-1 px-6 py-3 font-medium"
            >
              {t('users.cancel')}
            </Button>
            <Button
              variant="danger"
              onClick={confirmDelete}
              className="flex-1 px-6 py-3 font-medium"
            >
              <span className="material-icons text-sm mr-2">delete</span>
              {t('users.delete')}
            </Button>
          </div>
        </div>
      </Modal>
    </FullWidthContainer>
  );
};

export { Users };
